import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Skeleton, Typography } from '@chilecompra/react-kit/components'
import { formatPercentage, useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { onGetClaimInformationChartThunk } from './ClaimsReceivedSection.actions'
import { PUBLIC_URL } from '../../config/settings/environment'
import { useTheme } from 'styled-components'
import PieChart from '../../components/Charts/PieChart'
import {
  Card,
  CardItem,
  Column,
  ColumnChart,
  NotFoundImg,
  Root,
  Row,
  RowChart,
  Strong,
  Separator,
  WarningImg
} from './claimsReceivedSection.styles'
import { formatAmount } from '../../modules/utils/formatters'

/**
 * The Claims section container.
 */
const ClaimsReceivedSection = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const {
    size: { isTiny }
  } = useViewport()

  const handlePieChartTooltipFormatter = useCallback(function () {
    return `
        <p>
          <strong>  ${formatPercentage(this.point.percentage)}</strong> ${this.key}
        </p>
      `
  }, [])

  const { error, loading, chartData, rangeDate, hasData, paymentNotTime, claimsIrregularity } = useSelector(
    state => state.claimInformation
  )

  const startDate = `${rangeDate?.initialMonth} ${rangeDate?.initialYear} `
  const endDate = `${rangeDate?.finalMonth} ${rangeDate?.finalYear}`
  const lastUpdate = chartData[0]?.dateUpdate

  const isContentReady = !loading && !error
  const hasDataToShow = isContentReady && hasData

  useEffectOnce(() => {
    dispatch(onGetClaimInformationChartThunk(dni))
  }, [dispatch, dni])

  return (
    <Root>
      {loading && !error && (
        <>
          <Row flexDirection="column" gap="10px" margin="20px 0 0 0">
            <Skeleton height="35px" variant="rectangular" width="20%" />
            <Skeleton height="15px" variant="rectangular" width="60%" />
            <Skeleton height="59px" variant="rectangular" width="70%" />
          </Row>
          <Row flexDirection="column" margin="24px 0 30px 0">
            <Skeleton height="440px" variant="rectangular" width="100%" />
          </Row>
        </>
      )}
      {!loading && (
        <>
          <Column width="100%" margin="10px 0 24px 0">
            <Typography color="black1" variant="h4" fontWeight="bold" fontSize="18px" lineHeight="22px" tabIndex="0">
              Reclamos recibidos
            </Typography>
            <Typography
              color="black3"
              display="inline"
              variant="subtitle"
              fontWeight="regular"
              fontSize="14px"
              lineHeight="18px"
              tabIndex="0"
            >
              <Strong>Fuente:</Strong> Reclamos ingresados a través del Centro de Ayuda de Mercado Público.
            </Typography>
          </Column>
          {hasDataToShow && (
            <Column maxWidth="590px">
              <Typography
                color="black1"
                fontSize="16px"
                variant="body1"
                lineHeight="21px"
                fontWeight="regular"
                tabIndex="0"
              >
                Número de reclamos que ha recibido el organismo comprador en base a todos sus procesos de compra durante
                el periodo de <Strong>{startDate} </Strong> a <Strong>{endDate}. </Strong>
              </Typography>
            </Column>
          )}
        </>
      )}
      {error && (
        <Card
          display="flex"
          enableBorder
          color="blank"
          margin="43px 0 44px 0"
          alignItems="center"
          height={isTiny ? '139px' : '243px'}
        >
          <CardItem direction="row" padding="25px 16px">
            <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
            <Typography
              color="black1"
              variant="body1"
              fontWeight="regular"
              fontSize="16px"
              lineHeight="21px"
              margin="0 0 0 33px"
              tabIndex="0"
            >
              No ha sido posible obtener información de la institución.
              <br />
              Por favor, te pedimos que lo vuelvas a intentar dentro de unos minutos.
            </Typography>
          </CardItem>
        </Card>
      )}
      {!error && !loading && !hasData && (
        <Card margin="24px 0 30px 0" display="flex" enableBorder color="blank" textAlign="center">
          <NotFoundImg
            alt="No se encontraron resultados"
            margin="48px 0 0 0"
            src={`${PUBLIC_URL}/assets/images/notFound.svg`}
          />
          <Typography
            color="black1"
            variant="h4"
            fontWeight="bold"
            fontSize="18px"
            lineHeight="22px"
            margin="10px"
            tabIndex="0"
          >
            No existe información
          </Typography>
          <Typography
            color="black3"
            variant="body2"
            fontWeight="regular"
            fontSize="14px"
            lineHeight="18px"
            margin="5px 0 52px 0"
            tabIndex="0"
          >
            El organismo comprador no ha recibido
            <br />
            reclamos durante los últimos 12 meses.
          </Typography>
        </Card>
      )}
      {hasDataToShow && (
        <RowChart justifyContent="space-around" margin="24px 0 30px 0">
          <ColumnChart
            lg={6}
            sm={7}
            xs={12}
            alignContent="center"
            flexWrap="wrap"
            justifyContent="center"
            padding="2px 2px"
          >
            {!loading && (
              <PieChart
                data={chartData?.map(e => {
                  return {
                    ...e,
                    y: e.orders
                  }
                })}
                minSize={isTiny ? '30' : '40'}
                size="50%"
                spacing={isTiny ? [-20, 0, -10, 0] : [0, 0, 0, 5]}
                hoverColor={theme.palette.brand.primary}
                id="regional-amounts-traded-pie-chart"
                tooltipFormatter={handlePieChartTooltipFormatter}
              />
            )}
          </ColumnChart>

          <ColumnChart flexDirection="column" justifyContent="center" lg={5} sm={5} xs={12}>
            <Row justifyContent="space-around">
              {loading ? (
                <Skeleton variant="rectangular" width="100%" />
              ) : (
                <>
                  <ColumnChart lg={9} sm={8} xs={8} justifyContent="left">
                    <Typography fontWeight="bold" fontSize="24px" lineHeight="18px" tabIndex="0" margin="8px 0">
                      {formatAmount(paymentNotTime)}
                    </Typography>
                  </ColumnChart>

                  <ColumnChart lg={9} sm={8} xs={8}>
                    <Typography
                      align="left"
                      color="black1"
                      variant="body1"
                      fontSize="16px"
                      lineHeight="21px"
                      tabIndex="0"
                    >
                      Reclamos por pago no oportuno
                    </Typography>
                  </ColumnChart>
                </>
              )}
            </Row>
            <Separator />
            <Row justifyContent="space-around">
              {loading ? (
                <Skeleton variant="rectangular" width="100%" />
              ) : (
                <>
                  <ColumnChart lg={9} sm={8} xs={8} justifyContent="left">
                    <Typography fontWeight="bold" fontSize="24px" lineHeight="18px" margin="8px 0" tabIndex="0">
                      {formatAmount(claimsIrregularity)}
                    </Typography>
                  </ColumnChart>

                  <ColumnChart lg={9} sm={8} xs={8}>
                    <Typography
                      align="left"
                      color="black1"
                      variant="body1"
                      fontSize="16px"
                      lineHeight="21px"
                      tabIndex="0"
                    >
                      Reclamos por irregularidad en el proceso de compra
                    </Typography>
                  </ColumnChart>
                </>
              )}
            </Row>
          </ColumnChart>

          {loading ? (
            <Skeleton variant="rectangular" width="100%" />
          ) : (
            <Row justifyContent="left">
              <Column xs={12} md={12} lg={12}>
                <Typography
                  color="black3"
                  variant="body2"
                  fontWeight="regular"
                  fontSize="14px"
                  lineHeight="18px"
                  tabIndex="0"
                  sx={{ margin: { xs: '20px 0 24px 30px', sm: '0 0 24px 30px' } }}
                >
                  Última actualización: {lastUpdate}
                </Typography>
              </Column>
            </Row>
          )}
        </RowChart>
      )}
    </Root>
  )
}
export default ClaimsReceivedSection
